/**
 * Mock Data for RAE Project Management
 * Hierarchical project structure for Eplan Tracker demonstration
 */

import {
  User,
  Project,
  Zone,
  Section,
  Element,
  SubElement,
  TimeEntry,
  TaskComment,
  TaskFile,
  StatusHistoryEntry,
  TaskStatus,
  Priority,
  SidebarSection,
} from '../types';
import { getAvatarColor, averageProgress } from '../utils/format';

// ==================== MOCK USERS ====================

const RAW_USERS: Omit<User, 'avatarColor'>[] = [
  { id: 'pm-001', name: 'David Müller', email: 'david.muller@rae.com', role: 'PROJECT_MANAGER', allowedSections: ['dashboard', 'workspace', 'my-tasks', 'team', 'reports', 'settings', 'calendar'] as SidebarSection[] },
  { id: 'pm-002', name: 'Sarah Chen', email: 'sarah.chen@rae.com', role: 'PROJECT_MANAGER', allowedSections: ['dashboard', 'workspace', 'my-tasks', 'team', 'reports', 'settings', 'calendar'] as SidebarSection[] },
  { id: 'tech-001', name: 'James Rodriguez', email: 'james.rodriguez@rae.com', role: 'TECHNICIAN', allowedSections: ['my-tasks', 'calendar'] as SidebarSection[] },
  { id: 'tech-002', name: 'Anna Petrov', email: 'anna.petrov@rae.com', role: 'TECHNICIAN', allowedSections: ['my-tasks', 'calendar'] as SidebarSection[] },
  { id: 'tech-003', name: 'Michael Torres', email: 'michael.torres@rae.com', role: 'TECHNICIAN', allowedSections: ['my-tasks', 'calendar'] as SidebarSection[] },
  { id: 'tech-004', name: 'Lisa Nakamura', email: 'lisa.nakamura@rae.com', role: 'TECHNICIAN', allowedSections: ['my-tasks', 'calendar'] as SidebarSection[] },
  { id: 'tech-005', name: 'Robert Kim', email: 'robert.kim@rae.com', role: 'TECHNICIAN', allowedSections: ['my-tasks', 'calendar'] as SidebarSection[] },
  { id: 'tech-006', name: 'Elena Vasquez', email: 'elena.vasquez@rae.com', role: 'TECHNICIAN', allowedSections: ['my-tasks', 'calendar'] as SidebarSection[] },
];

export const MOCK_USERS: User[] = RAW_USERS.map(u => ({
  ...u,
  avatarColor: getAvatarColor(u.id),
}));

// Mutable cache — seeded with real Supabase users by ProjectContext once loaded
let _usersCache: { id: string; name: string }[] = MOCK_USERS;

export function setUsersCache(users: { id: string; name: string }[]): void {
  if (users.length > 0) _usersCache = users;
}

// Helper to get user name by ID (uses real users once ProjectContext seeds the cache)
export const getUserName = (userId: string): string => {
  return _usersCache.find(u => u.id === userId)?.name || 'Unknown';
};

// ==================== SUB-ELEMENTS (TASKS) ====================

const createSubElement = (
  id: string,
  name: string,
  code: string,
  description: string,
  elementId: string,
  status: TaskStatus,
  priority: Priority,
  assignedTo: string[],
  estimatedHours: number,
  startDate: string,
  endDate: string,
  timeEntries: TimeEntry[] = [],
  comments: TaskComment[] = [],
  attachments: TaskFile[] = [],
  statusHistory: StatusHistoryEntry[] = [],
): SubElement => ({
  id, name, code, description, elementId,
  status, priority, assignedTo, estimatedHours,
  startDate, endDate,
  timeEntries, comments, attachments, statusHistory,
  createdAt: '2026-01-15T08:00:00Z',
  updatedAt: '2026-02-20T14:30:00Z',
});

// ==================== DATA GENERATION HELPERS ====================

const _STATUSES: TaskStatus[] = [
  'ASSIGNED', 'STARTED', 'ASSIGNED', 'IN_PROGRESS', 'ASSIGNED',
  'FINISHED', 'ASSIGNED', 'PAUSED', 'ASSIGNED', 'ISSUES',
];
const _PRIORITIES: Priority[] = ['MEDIUM', 'HIGH', 'MEDIUM', 'LOW', 'HIGH', 'CRITICAL', 'MEDIUM', 'HIGH'];
const _TECHS = ['tech-001', 'tech-002', 'tech-003', 'tech-004', 'tech-005', 'tech-006'];

let _seN = 0;
let _elN = 0;

const makeSEs = (elId: string, codes: string[]): SubElement[] =>
  codes.map((c, i) => {
    _seN++;
    const t = _seN % 6;
    // Generate staggered dates within the project timeline
    const baseDate = new Date('2026-01-20');
    const startOffset = Math.floor(_seN / 3) * 2 + (i % 4);
    const duration = 3 + (_seN % 5);
    const start = new Date(baseDate);
    start.setDate(start.getDate() + startOffset);
    const end = new Date(start);
    end.setDate(end.getDate() + duration);
    return createSubElement(
      `se-${String(_seN).padStart(3, '0')}`, c, c, '', elId,
      _STATUSES[_seN % 10], _PRIORITIES[_seN % 8],
      _seN % 7 === 0 ? [_TECHS[t], _TECHS[(t + 1) % 6]] : [_TECHS[t]],
      1 + (_seN % 4),
      start.toISOString().split('T')[0],
      end.toISOString().split('T')[0],
    );
  });

const makeEl = (name: string, secId: string, codes: string[]): Element => {
  _elN++;
  const id = `el-${String(_elN).padStart(3, '0')}`;
  return { id, name, code: name, description: '', sectionId: secId, subElements: makeSEs(id, codes) };
};

// ==================== P0350V Electrical - Sections ====================

// Section 1: PDP Power Supply
const secPDP: Section = {
  id: 'sec-001',
  name: 'PDP Power Supply',
  code: 'PDP',
  description: 'Power Distribution Panels',
  zoneId: 'zone-001',
  elements: [
    makeEl('PDP01', 'sec-001', ['MC01', 'HS00010-PSU01', 'HS00010-PSU02', 'HS00010.HB01-VFD01', 'HS00010.HB01-VFD02', 'HS00010.HB02-VFD01', 'HS00010.HB01-VFD02', 'MCB09', 'HS00010-BCP01', 'HS00010-BCP02']),
    makeEl('PDP02', 'sec-001', ['CS00010-PSU01', 'CS00010-PSU02', 'CS00010.FE01-VFD01', 'CS00010.FE02-VFD01', 'CS00010.FS01-VFD01', 'CS00010.FS02-VFD01']),
    makeEl('PDP03', 'sec-001', ['HS00020-PSU01', 'HS00020-PSU01', 'HS00020.HB01-VFD01', 'HS00020.HB01-VFD02', 'HS00020.HB02-VFD01', 'HS00020.HB02-VFD02', 'HS00020.FE01-VFD01', 'HS00020.BCP01', 'HS00020.BCP02']),
    makeEl('PDP04', 'sec-001', ['CS00020.PSU01', 'CS00020.PSU2', 'CS00020.FE01-VFD01', 'CS00020.FE02-VFD1', 'CS00020.FS01']),
    makeEl('PDP05', 'sec-001', ['HS00030-PSU01', 'HS00030-PSU02', 'HS00030.HB01-VFD01', 'HS00030.HB01-VFD02', 'HS00030.HB02-VFD01']),
    makeEl('PDP06', 'sec-001', ['CS0030-PSU01', 'CS0030-PSU02', 'CS0030.FE01-VFD01', 'CS0030.FE02-VFD01', 'CS0030.FS01-VFD01', 'CS0030.FE02-VFD01']),
  ],
};

// Section 2: 24 VDC
const sec24VDC: Section = {
  id: 'sec-002',
  name: '24 VDC',
  code: '24VDC',
  description: '24 VDC Power Distribution',
  zoneId: 'zone-001',
  elements: [
    makeEl('HS00010-PSU01', 'sec-002', ['HB01', 'TH01', 'FE01', 'TS01']),
    makeEl('HS00010-PSU02', 'sec-002', ['HMI', 'HB02', 'TH02', 'TS02']),
    makeEl('CS00010-PSU01', 'sec-002', ['FE01', 'A101', 'CW01', 'FS01', 'AH01', 'TC01']),
    makeEl('CS00010-PSU02', 'sec-002', ['FE02', 'A102', 'CW02', 'FS02', 'AH02']),
    makeEl('HS00020-PSU01', 'sec-002', ['HB01', 'TH01']),
    makeEl('HS00020-PSU02', 'sec-002', ['HM102', 'HB02', 'TH02', 'TS02']),
    makeEl('CS00020-PSU01', 'sec-002', ['FE01', 'A101', 'CW01', 'FS01', 'AH01', 'TC01']),
    makeEl('CS00020-PSU02', 'sec-002', ['FE02', 'A102', 'CW02', 'FS02', 'AH02']),
    makeEl('HS00030-PSU01', 'sec-002', ['HB01', 'TH01', 'FE01', 'TS01']),
    makeEl('HS00030-PSU02', 'sec-002', ['HB02', 'TH02', 'FE02']),
    makeEl('HS00030-PSU03', 'sec-002', ['HM103', 'HB03', 'TH03', 'TS02']),
    makeEl('CS00030-PSU01', 'sec-002', ['FE01', 'A101', 'CW01', 'FS01', 'AH01', 'TC01']),
    makeEl('CS00030-PSU02', 'sec-002', ['FE02', 'A102', 'CW02', 'FS02', 'AH02']),
  ],
};

// Section 3: Profynet
const secProfynet: Section = {
  id: 'sec-003',
  name: 'Profynet',
  code: 'PROFYNET',
  description: 'Profynet Network Configuration',
  zoneId: 'zone-001',
  elements: [
    makeEl('MCP01', 'sec-003', ['MCP', 'HS00010', 'CS00010', 'HS00020', 'CS00020', 'HS00030', 'CS00030']),
    makeEl('HS00010', 'sec-003', ['HM101', 'PDP01', 'HB01', 'BCP01', 'BCP02', 'HB02', 'FE01', 'TH01', 'TS01', 'TS02']),
    makeEl('CS00010', 'sec-003', ['PDP02', 'CS00010.FE01', 'CS00010.FS01', 'CS00010.AH01', 'CS00010.AI01', 'CS00010.TC01', 'CS00010.FE02']),
    makeEl('HS00020', 'sec-003', ['PDP03', 'HS00020.HB01', 'HS00020.BCP01', 'HS00020.BCP02', 'HS00020.HB02', 'HS00020.FE01', 'HS00020.TH01', 'HS00020.TS01', 'HS00020.TS02']),
    makeEl('CS00020', 'sec-003', ['PDP04', 'CS00020.FE01', 'CS00020.FS01', 'CS00020.AH01', 'CS00020.A101', 'CS00020.TC01', 'CS00020.FE02', 'CS00020.AH02', 'CS00020.FS02', 'CS00020.A102']),
    makeEl('HS00030', 'sec-003', ['HM103', 'PDP05', 'HS00030.HB01', 'HS00030.BCP01', 'HS00030.FE01', 'HS00030.HB02', 'HS00030.BCP02', 'HS00030.FE02', 'HS00030.HB03', 'HS00030.BСРОЗ', 'HS00030.TH01']),
    makeEl('CS00030', 'sec-003', ['PDP06', 'CS00030.FE01', 'CS00030.FS01', 'CS00030.AH01', 'CS00030.AI01', 'CS00030.TC01', 'CS00030.FE02', 'CS00030.AH02', 'CS00030.FS02', 'CS00030.AI02']),
  ],
};

// Section 4: IO Modules
const secIO: Section = {
  id: 'sec-004',
  name: 'IO Modules',
  code: 'IO',
  description: 'Input/Output Modules',
  zoneId: 'zone-001',
  elements: [
    makeEl('HS00010-HB01', 'sec-004', ['PSU01', 'PDIT01', 'PDIT02', 'PDIT03', 'VT01', 'VT02']),
    makeEl('HS00010-HB02', 'sec-004', ['PSU01', 'PDIT01', 'PDIT02', 'PDIT03', 'VT01', 'VT02']),
    makeEl('HS00010-TH01', 'sec-004', ['TT01', 'TT02']),
    makeEl('HS00010-TH02', 'sec-004', ['TT01', 'TT02']),
    makeEl('HS00010-TS01', 'sec-004', ['FV01', 'ZSC01', 'ZSO01', 'FV02', 'ZSC02', 'ZSO02', 'PDIT01']),
    makeEl('HS00010-TS02', 'sec-004', ['FV01', 'ZSC01', 'ZSO01', 'FV02', 'ZSC02', 'ZSO02', 'PDIT01']),
    makeEl('HS00010-FE01', 'sec-004', ['FV01', 'FV02', 'ZT01', 'ZT02', 'PDIT01', 'VT01']),
    makeEl('CS00010-FS01', 'sec-004', ['PSU01', 'PDIT01', 'TT01', 'VT01', 'VT02']),
    makeEl('CS00010-FS02', 'sec-004', ['PSU01', 'PDIT01', 'TT01', 'VT01', 'VT02']),
    makeEl('CS00010-FE01', 'sec-004', ['PDIT01', 'TT01', 'VT01', 'VT02', 'FV01', 'FV02', 'FV03', 'FV04', 'ZT01', 'ZT02', 'ZT03']),
    makeEl('CS00010-FE02', 'sec-004', ['PDIT01', 'TT01', 'VT01', 'VT02', 'FV01', 'FV02', 'FV03', 'FV04', 'ZT01', 'ZT02', 'ZT03']),
    makeEl('CS00010-AH01', 'sec-004', ['PDIT01', 'TT01', 'TT02']),
    makeEl('CS00010-AH02', 'sec-004', ['PDIT01', 'TT01', 'TT02']),
    makeEl('CS00010-AI01', 'sec-004', ['PV01', 'ZT01', 'TT01']),
    makeEl('CS00010-AI02', 'sec-004', ['PV01', 'ZT01', 'TT01']),
    makeEl('CS00010-CW01', 'sec-004', ['PIT01', 'PIT01', 'TT01', 'TT02', 'TV01']),
    makeEl('CS00010-CW02', 'sec-004', ['PIT01', 'PIT01', 'TT01', 'TT02', 'TV01']),
    makeEl('CS00010-TC01', 'sec-004', ['ZSC01', 'ZSC02', 'ZSC03', 'ZSC04', 'GS01', 'GS02']),
    makeEl('HS00020-HB01', 'sec-004', ['PSU', 'PDIT01', 'PDIT02', 'PDIT03', 'VT01', 'VT02']),
    makeEl('HS00020-HB02', 'sec-004', ['PSU', 'PDIT01', 'PDIT02', 'PDIT03', 'VT01', 'VT02']),
    makeEl('HS00020-TH01', 'sec-004', ['TT01', 'TT02']),
    makeEl('HS00020-TH02', 'sec-004', ['TT01', 'TT02']),
    makeEl('HS00020-TS01', 'sec-004', ['FV01', 'FV02', 'ZSC01', 'ZSC02', 'ZSC03', 'ZSC4', 'PDIT01']),
    makeEl('HS00020-TS02', 'sec-004', ['FV01', 'ZSC01', 'ZSC02', 'PDIT01']),
    makeEl('HS00020-FE01', 'sec-004', ['FV01', 'FV02', 'ZT01', 'ZT02', 'PDIT01', 'VT01']),
    makeEl('CS00020-FS01', 'sec-004', ['PSU', 'PDIT01', 'TT01', 'VT01', 'VT02']),
    makeEl('CS00020-FS02', 'sec-004', ['PSU', 'PDIT01', 'TT01', 'VT01', 'VT02']),
    makeEl('CS00020-FE01', 'sec-004', ['PDIT01', 'TT01', 'VT01', 'VT02', 'FV01', 'FV02', 'ZT01', 'ZT02']),
    makeEl('CS00020-FE02', 'sec-004', ['PDIT01', 'TT01', 'VT01', 'VT02', 'FV01', 'FV02', 'FV03', 'ZT01', 'ZT02', 'ZT03']),
    makeEl('HS00030-HB01', 'sec-004', ['PSU', 'PDIT01', 'PDIT02', 'PDIT03', 'VT01', 'VT02']),
    makeEl('HS00030-HB02', 'sec-004', ['PSU', 'PDIT01', 'PDIT02', 'PDIT03', 'VT01', 'VT02']),
    makeEl('HS00030-HB03', 'sec-004', ['PSU', 'PDIT01', 'PDIT02', 'PDIT03', 'VT01', 'VT02']),
    makeEl('HS00030-TH01', 'sec-004', ['TT01', 'TT02']),
    makeEl('HS00030-TS01', 'sec-004', ['FV01', 'FV02', 'ZSC01', 'ZSC02', 'ZSC03', 'ZSC04', 'PDIT01']),
    makeEl('HS00030-TS02', 'sec-004', ['ZSC01', 'ZSC01', 'PDIT01']),
    makeEl('HS00030-FE01', 'sec-004', ['FV01', 'ZT01', 'PDIT01', 'VT01']),
    makeEl('HS00030-FE02', 'sec-004', ['FV01', 'ZT01', 'PDIT01', 'VT01']),
    makeEl('CS00030-FS01', 'sec-004', ['PSU', 'PDIT01', 'TT01', 'VT01', 'VT02']),
    makeEl('CS00030-FS02', 'sec-004', ['PSU', 'PDIT01', 'TT01', 'VT01', 'VT02']),
    makeEl('CS00030-FE01', 'sec-004', ['PDIT01', 'TT01', 'VT01', 'VT02', 'FV01', 'VF02', 'ZT01', 'ZT02']),
    makeEl('CS00030-FE02', 'sec-004', ['PDIT01', 'TT01', 'VT01', 'VT02', 'FV01', 'FV02', 'FV03', 'ZT01', 'ZT02', 'ZT03']),
    makeEl('CS00030-AH01', 'sec-004', ['PDIT01', 'TT01', 'TT02']),
    makeEl('CS00030-AH02', 'sec-004', ['PDIT01', 'TT01', 'TT02']),
    makeEl('HS00010-BCP01', 'sec-004', ['FQIT01', 'PDS01', 'PDS02', 'GS01', 'TE01', 'PDS01', 'ZSC01', 'ZSC02', 'TV01', 'FZV01', 'FZV02', 'BX01', 'PS01', 'PSH01', 'PSL03', 'PSL02', 'BE01', 'FZV03', 'FZV04']),
    makeEl('HS00010-BCP02', 'sec-004', ['FQIT01', 'PDS01', 'PDS02', 'GS01', 'TE01', 'TV01', 'FZV01', 'FZV02', 'BX01', 'PS01', 'PSH01', 'PSL03', 'PSL02', 'BE01', 'FZV03', 'FZV04']),
    makeEl('HS00020-BCP01', 'sec-004', ['FQIT01', 'PDS01', 'PDS02', 'GS01', 'TE01', 'TV01', 'FZV01', 'FZV02', 'BX01', 'PS01', 'PSH01', 'PSL03', 'PSL02', 'BE01', 'FZV03', 'FZV04']),
    makeEl('HS00020-BCP02', 'sec-004', ['FQIT01', 'PDS01', 'PDS02', 'GS01', 'TE01', 'TV01', 'FZV01', 'FZV02', 'BX01', 'PS01', 'PSH01', 'PSL03', 'PSL02', 'BE01', 'FZV03', 'FZV04']),
    makeEl('HS00030-BCP01', 'sec-004', ['FQIT01', 'PDS01', 'PDS02', 'GS01', 'TE01', 'PDS01', 'ZSC01', 'ZSC02', 'TV01', 'FZV01', 'FZV02', 'BX01', 'PS01', 'PSH01', 'PSL03', 'PSL02', 'BE01', 'FZV03', 'FZV04']),
    makeEl('HS00030-BCP02', 'sec-004', ['PDS01', 'PDS02', 'GS01', 'TE01', 'TV01', 'FZV01', 'FZV02', 'BX01', 'PS01', 'PSH01', 'PSL03', 'PSL02', 'BE01', 'FZV03', 'FZV04']),
    makeEl('HS00030-BСРОЗ', 'sec-004', ['FQIT01', 'PDS01', 'PDS02', 'GS01', 'TE01', 'PDS01', 'ZSC01', 'ZSC02', 'TV01', 'FZV01', 'FZV02', 'BX01', 'PS01', 'PSH01', 'PSL03', 'PSL02', 'BE01', 'FZV03', 'FZV04']),
  ],
};

// ==================== ZONE ====================

const zones: Zone[] = [
  {
    id: 'zone-001',
    name: 'P0350V Electrical',
    code: 'P0350V',
    description: 'Main electrical installation zone covering power distribution, motor control, and PLC systems',
    projectId: 'proj-001',
    sections: [secPDP, sec24VDC, secProfynet, secIO],
    createdAt: '2026-01-10T08:00:00Z',
    updatedAt: '2026-02-20T16:00:00Z',
  },
];

// ==================== MAIN PROJECT ====================

export const MOCK_PROJECTS: Project[] = [
  {
    id: 'proj-001',
    name: 'Houston Processing Plant Electrical Upgrade',
    code: 'PAU-P2',
    description: 'Complete electrical and instrumentation upgrade for processing plant. Includes power distribution, motor controls, PLC programming, and field instrumentation.',
    client: 'Tesla',
    status: 'ACTIVE',
    startDate: '2026-01-15',
    endDate: '2026-12-31',
    zones: zones,
    teamMembers: ['pm-001', 'pm-002', 'tech-001', 'tech-002', 'tech-003', 'tech-004', 'tech-005', 'tech-006'],
    createdAt: '2026-01-10T08:00:00Z',
    updatedAt: '2026-02-20T16:00:00Z',
  },
];

// ==================== UTILITY: Flatten all sub-elements ====================

export function getAllSubElements(project: Project): SubElement[] {
  const result: SubElement[] = [];
  for (const zone of project.zones) {
    for (const section of zone.sections) {
      for (const element of section.elements) {
        result.push(...element.subElements);
      }
    }
  }
  return result;
}

export function getSubElementPath(project: Project, subElementId: string): {
  zone: Zone;
  section: Section;
  element: Element;
  subElement: SubElement;
} | null {
  for (const zone of project.zones) {
    for (const section of zone.sections) {
      for (const element of section.elements) {
        const subElement = element.subElements.find(se => se.id === subElementId);
        if (subElement) {
          return { zone, section, element, subElement };
        }
      }
    }
  }
  return null;
}

export function getUserTasks(project: Project, userId: string): SubElement[] {
  return getAllSubElements(project).filter(se => se.assignedTo.includes(userId));
}

export function calculateActualHours(timeEntries: TimeEntry[]): number {
  let totalMinutes = 0;
  for (const entry of timeEntries) {
    if (entry.endTime) {
      const start = new Date(entry.startTime).getTime();
      const end = new Date(entry.endTime).getTime();
      totalMinutes += (end - start) / 60000 - entry.pausedDuration;
    }
  }
  return Math.round((totalMinutes / 60) * 100) / 100;
}

export function calculateElementProgress(element: Element): number {
  const total = element.subElements.length;
  if (total === 0) return 0;
  const finished = element.subElements.filter(se => se.status === 'FINISHED').length;
  return Math.round((finished / total) * 100);
}

// Progreso "clásico": % de tareas terminadas. Es el número que el equipo
// lleva usando y que aparece en Dashboard, Reportes y Workspace — no se toca.
export function calculateSectionProgress(section: Section): number {
  const allSubs = section.elements.flatMap(el => el.subElements);
  const total = allSubs.length;
  if (total === 0) return 0;
  const finished = allSubs.filter(se => se.status === 'FINISHED').length;
  return Math.round((finished / total) * 100);
}

export function calculateZoneProgress(zone: Zone): number {
  const allSubs = zone.sections.flatMap(sec => sec.elements.flatMap(el => el.subElements));
  const total = allSubs.length;
  if (total === 0) return 0;
  const finished = allSubs.filter(se => se.status === 'FINISHED').length;
  return Math.round((finished / total) * 100);
}

export function calculateProjectProgress(project: Project): number {
  const allSubs = getAllSubElements(project);
  const total = allSubs.length;
  if (total === 0) return 0;
  const finished = allSubs.filter(se => se.status === 'FINISHED').length;
  return Math.round((finished / total) * 100);
}

/**
 * Avance ponderado: promedia el progreso de cada tarea según su estado, así
 * el trabajo en curso también cuenta. Métrica NUEVA y separada — conviene
 * mostrarla junto al progreso clásico, nunca en su lugar.
 */
export function calculateProjectWeightedProgress(project: Project): number {
  return averageProgress(getAllSubElements(project));
}
